import { useEffect, useState, useContext, forwardRef } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as regularHeart } from "@fortawesome/free-regular-svg-icons";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import AOS from "aos";
import "aos/dist/aos.css"; // Import AOS styles
import { CartContext } from "../../Context/CartContext";
import { LikedProductContext } from "../../Context/LikedProductContext";
import { FaSpinner } from "react-icons/fa"; // Import Spinner
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { FaStar } from "react-icons/fa";
import { StarRate } from "@mui/icons-material";
import { useAuth } from "../../Context/AuthContext";

export const Rating = ({ averageRating, totalReviews }) => {
  const fillPercentage = (averageRating / 5) * 100; // Convert rating to percentage

  return (
    <span className="d-flex align-items-center">
      <span
        style={{
          position: "relative",
          display: "inline-block",
        }}
      >
        {/* Background star (gray) */}
        <StarRate style={{ color: "#e0e0e0", fontSize: "22px" }} />
        {/* Filled star (orange) */}
        <span
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${fillPercentage}%`,
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <StarRate style={{ color: "orange", fontSize: "22px" }} />
        </span>
      </span>
      <span
        className="text-dark ms-1 pt-1"
        style={{ fontSize: "14px", color: "#111111", fontWeight: 500 }}
      >
        {averageRating.toFixed(1)} ({totalReviews})
      </span>
    </span>
  );
};

const Product = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { loginToken } = useAuth();
  const { cartItems, addToCart, buyNowProduct } = useContext(CartContext);
  const { likedItems, addLikeProduct, removeFromFavorite } =
    useContext(LikedProductContext);
  const {
    name,
    description,
    price,
    image,
    productId,
    percentOff,
    in_stock,
    averageRating,
    variation_options,
  } = props.product;
  const { enqueueSnackbar } = useSnackbar();

  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Determine price to display
  const productPrice =
    variation_options && variation_options.length > 0
      ? variation_options[0].price // Use price of the first variant
      : price; // Fallback to base product price

  let offPrice = `€${productPrice}`;

  if (percentOff && percentOff > 0) {
    offPrice = (
      <>
        <del className="text-danger">€{productPrice}</del> €{" "}
        {productPrice - (percentOff * productPrice) / 100}
      </>
    );
  }

  // Initialize AOS when the component is mounted
  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  // Check if the product is liked and set isFavorited on mount
  useEffect(() => {
    const productInLikedItems = likedItems.some(
      (item) => item.productId === productId
    );
    setIsFavorited(productInLikedItems);
  }, [likedItems, productId]);

  // Function to add the product to cart
  const handleAddToCart = async () => {
    setIsLoading(true); // Start loading when the button is clicked
    try {
      // Assuming you have access to the current cart items in a `cartItems` array
      const existingCartItem = cartItems.find(
        (item) => item.productId === props.product.productId
      );

      if (existingCartItem) {
        // Product already exists in the cart, update quantity
        const updatedQuantity = existingCartItem.quantity + 1; // Update quantity based on the existing quantity
        await addToCart({
          product: props.product,
          variant_id: null,
          newQuantity: updatedQuantity,
        });
        setQuantity(updatedQuantity); // Update local state quantity
      } else {
        // Product does not exist in the cart, add as a new item
        await addToCart({
          product: props.product,
          variant_id: null,
          newQuantity: quantity, // Assuming `quantity` is the default or initial quantity
        });
        setIsAddedToCart(true); // Set product as added to cart
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setIsLoading(false); // Stop loading after the action is complete
    }
  };

  const handleBuyNow = async () => {
    if (!loginToken) {
      enqueueSnackbar("Kindly Login To your account to buy product instantly", {
        variant: "info",
        autoHideDuration: 2500,
      });
      return;
    }

    if (typeof buyNowProduct !== "function") {
      console.error("buyNowProduct is not a function");
      return;
    }

    if (!props.product) {
      console.error("Product data is missing");
      return;
    }

    buyNowProduct({
      product: props.product,
      variant_id: null,
    });
  };

  // Toggle favorite state using API-based methods
  const toggleFavorite = async () => {
    try {
      setIsFavorited((prev) => !prev); // Toggle state immediately for visual feedback

      if (!isFavorited) {
        await addLikeProduct(props.product); // Add product to favorites
      } else {
        await removeFromFavorite(props.product); // Remove product from favorites
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      setIsFavorited((prev) => !prev); // Revert state in case of error
    }
  };

  return (
    <div
      ref={ref}
      className={`${
        props.isCategoryPage
          ? ""
          : `col-6 ${props?.showFilters ? "col-xl-4" : "col-xl-3"}`
      }`}
    >
      <div className="card rounded-3 shadow-sm product-card position-relative">
        <Link to={`/products/${productId}`}>
          {percentOff > 0 && (
            <div
              className="badge bg-green-theme py-2 text-white position-absolute"
              style={{ top: "0.5rem", right: "0.5rem" }}
            >
              {percentOff}% OFF
            </div>
          )}
          <div className="px-5 py-md-2 product-image-container position-relative d-flex justify-content-center">
            <img
              className="card-img-top product-image"
              alt={name}
              src={image.original}
            />
          </div>
        </Link>

        {in_stock ? (
          ""
        ) : (
          <div
            className="position-absolute fw-bold"
            style={{
              top: "5px",
              left: "15px",
              cursor: "pointer",
            }}
          >
            <span className="blinking-dot-detail-out-of-stock">●</span>{" "}
            <span className="text-danger">{t("Out-of-stock")}</span>
          </div>
        )}

        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-start w-100 h-fix-cardBody">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="card-title text-start text-dark text-truncate fs-5 fw-bold">
                  {name}
                </span>
                <FontAwesomeIcon
                  icon={isFavorited ? solidHeart : regularHeart}
                  className=""
                  style={{ cursor: "pointer" }}
                  onClick={toggleFavorite}
                />
              </div>
              <div className="d-flex align-items-center mb-3">
                <span className="d-flex align-items-center card-text text-start fs-5 fw-bold me-3">
                  {offPrice}
                </span>
                <Rating averageRating={3} totalReviews={120} />
              </div>
              <p className="text-muted text-start" style={{ fontSize: "16px" }}>
                {description.split(" ").slice(0, 12).join(" ") +
                  (description.split(" ").length > 12 ? "..." : "")}
              </p>
              <div className="d-flex justify-content-between flex-column flex-sm-row">
                <div className="col-12 d-flex flex-column flex-sm-row">
                  {variation_options && variation_options.length > 0 ? (
                    <Link
                      to={`/products/${productId}`}
                      className="btn btn-primary px-2 px-md-4"
                    >
                      {t("More Details")}
                    </Link>
                  ) : (
                    <>
                      <button
                        className="btn-stylish me-2 px-3 d-flex align-items-center justify-content-center mb-2 mb-sm-0"
                        onClick={() => {
                          if (in_stock && !isLoading) {
                            handleAddToCart(); // Proceed with adding to cart
                          } else if (!in_stock) {
                            enqueueSnackbar(
                              "⚠️ Currently product is out of stock.",
                              {
                                variant: "danger",
                                autoHideDuration: 1500,
                              }
                            ); // Show out-of-stock message
                          }
                        }}
                        style={
                          !in_stock
                            ? { opacity: 0.5, cursor: "not-allowed" }
                            : {}
                        }
                        disabled={isLoading} // Disable button while loading
                      >
                        {isLoading ? (
                          <FaSpinner className="spin" /> // Show spinner while loading
                        ) : isAddedToCart ? (
                          <>
                            <FontAwesomeIcon
                              icon={["fas", "cart-plus"]}
                              className="d-block d-sm-none"
                            />
                            <span className="d-none d-sm-block">
                              {t("Added-to-cart")}
                            </span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={["fas", "cart-plus"]}
                              className="d-block d-sm-none"
                            />
                            <span className="d-none d-sm-block">
                              {t("Add-to-Cart")}
                            </span>
                          </>
                        )}
                      </button>

                      <button
                        className="buy-now-new px-3 border-0"
                        onClick={() => {
                          if (in_stock && !isLoading) {
                            handleBuyNow(); // Proceed with adding to cart
                          } else if (!in_stock) {
                            enqueueSnackbar(
                              "⚠️ Currently product is out of stock.",
                              {
                                variant: "danger",
                                autoHideDuration: 1500,
                              }
                            ); // Show out-of-stock message
                          }
                        }}
                        style={
                          !in_stock
                            ? { opacity: 0.5, cursor: "not-allowed" }
                            : {}
                        }
                        disabled={isLoading}
                      >
                        Buy Now
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Product;
